/**
 * Resend verification email button with cooldown timer.
 * Uses shared utilities from resend-verification.ts for cooldown tracking and API calls.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { SendHorizontal } from "@/components/animate-ui/icons/send-horizontal";
import { Button } from "@/components/ui/button";
import {
	COOLDOWN_SECONDS,
	getCooldownRemaining,
	sendVerificationEmail,
} from "@/lib/api/resend-verification";
import { emailSchema } from "@/lib/validation/auth";

interface ResendVerificationButtonProps {
	email: string;
	variant?: "default" | "outline" | "ghost";
	className?: string;
}

export function ResendVerificationButton({
	email,
	variant = "outline",
	className,
}: ResendVerificationButtonProps) {
	const [cooldownRemaining, setCooldownRemaining] = useState(() => getCooldownRemaining(email));
	const [isResending, setIsResending] = useState(false);

	// Countdown timer
	useEffect(() => {
		if (cooldownRemaining <= 0) return;
		const interval = setInterval(() => {
			setCooldownRemaining((prev) => (prev <= 1 ? 0 : prev - 1));
		}, 1000);
		return () => clearInterval(interval);
	}, [cooldownRemaining]);

	const handleResend = async () => {
		if (cooldownRemaining > 0) return;
		const validation = emailSchema.safeParse(email.trim());
		if (!validation.success) {
			toast.error(validation.error.issues[0]?.message || "Invalid email address");
			return;
		}
		setIsResending(true);
		const result = await sendVerificationEmail(email);
		setIsResending(false);
		if (result.success) {
			setCooldownRemaining(COOLDOWN_SECONDS);
			toast.success("Verification email sent! Check your inbox.");
		} else {
			toast.error(result.error || "Failed to resend email");
		}
	};

	return (
		<Button
			type="button"
			variant={variant}
			onClick={handleResend}
			disabled={isResending || cooldownRemaining > 0}
			className={className}
		>
			<SendHorizontal animateOnHover className="mr-2 size-4" />
			{isResending
				? "Sending..."
				: cooldownRemaining > 0
					? `Resend in ${cooldownRemaining}s`
					: "Resend verification email"}
		</Button>
	);
}
